import { DeleteOutlined, SyncOutlined, UploadOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Col, Descriptions, Image, Row, Space, Spin, Tag, Typography, Upload } from "antd";
import { useParams } from "react-router-dom";
import { artistService } from "../services/artist.service";

const { Title } = Typography;

export function ArtistDetailPage() {
	const { id } = useParams();
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	const { data, isLoading } = useQuery({
		queryKey: ["artist", id],
		queryFn: () => artistService.get(Number(id)),
		enabled: !!id,
	});

	const uploadMutation = useMutation({
		mutationFn: (files: File[]) => artistService.uploadFaces(Number(id), files),
		onSuccess: () => { message.success("Rasmlar yuklandi"); queryClient.invalidateQueries({ queryKey: ["artist", id] }); },
		onError: () => message.error("Yuklashda xatolik"),
	});

	const deleteFaceMutation = useMutation({
		mutationFn: (faceId: number) => artistService.deleteFace(Number(id), faceId),
		onSuccess: () => { message.success("Rasm o'chirildi"); queryClient.invalidateQueries({ queryKey: ["artist", id] }); },
	});

	const syncMutation = useMutation({
		mutationFn: () => artistService.sync(Number(id)),
		onSuccess: (res) => message.success(res.message || "Sinxronlandi"),
	});

	if (isLoading) return <Spin size="large" className="flex justify-center mt-20" />;

	const artist = data?.data;
	if (!artist) return <div>Topilmadi</div>;

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<Title level={3} className="!mb-0">{artist.name} {artist.stage_name && `(${artist.stage_name})`}</Title>
				<Button icon={<SyncOutlined />} onClick={() => syncMutation.mutate()} loading={syncMutation.isPending}>
					AI ga sinxronlash
				</Button>
			</div>
			<Row gutter={16}>
				<Col span={10}>
					<Card title="Ma'lumotlar">
						<Descriptions column={1} bordered size="small">
							<Descriptions.Item label="Ism">{artist.name}</Descriptions.Item>
							<Descriptions.Item label="Sahna ismi">{artist.stage_name || "—"}</Descriptions.Item>
							<Descriptions.Item label="Janr">{artist.genre || "—"}</Descriptions.Item>
							<Descriptions.Item label="Telefon">{artist.phone || "—"}</Descriptions.Item>
							<Descriptions.Item label="Yaratilgan">{artist.created}</Descriptions.Item>
						</Descriptions>
					</Card>
				</Col>
				<Col span={14}>
					<Card
						title={`Yuz rasmlari (${artist.faces?.length || 0})`}
						extra={
							<Upload
								multiple
								accept="image/*"
								showUploadList={false}
								beforeUpload={(_, fileList) => {
									uploadMutation.mutate(fileList as unknown as File[]);
									return false;
								}}
							>
								<Button icon={<UploadOutlined />} loading={uploadMutation.isPending}>Rasm yuklash</Button>
							</Upload>
						}
					>
						<Space wrap size={16}>
							{artist.faces?.map((face: { id: number; image: string; is_primary: number; is_synced: number }) => (
								<div key={face.id} className="relative group">
									<Image src={`${import.meta.env.VITE_API_URL}${face.image}`} width={120} height={120} className="object-cover rounded" />
									<div className="mt-1 flex gap-1">
										{face.is_primary ? <Tag color="blue">Asosiy</Tag> : null}
										<Tag color={face.is_synced ? "green" : "orange"}>{face.is_synced ? "Sync" : "Kutilmoqda"}</Tag>
									</div>
									<Button
										size="small"
										danger
										icon={<DeleteOutlined />}
										className="mt-1"
										onClick={() => deleteFaceMutation.mutate(face.id)}
									>
										O'chirish
									</Button>
								</div>
							))}
							{(!artist.faces || artist.faces.length === 0) && (
								<div className="text-gray-400">Yuz rasmlari yuklanmagan</div>
							)}
						</Space>
					</Card>
				</Col>
			</Row>
		</div>
	);
}
